<script lang="ts" setup>
import { cn } from '@/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import type {
	CuratedListSimilarRegionsProps,
	RegionClickedPayload,
	SimilarRegionClickedPayload
} from '.';
import type Carousel from '../ui/carousel/Carousel.vue';
import RegionCard from './RegionCard.vue';
import { breakpointsTailwind } from '@vueuse/core';

const props = withDefaults(defineProps<CuratedListSimilarRegionsProps>(), {
	containerClass: '',
	isLoading: false,
	title: 'Explore outros imóveis nos bairros que inspiram esta coleção'
});

const emit = defineEmits<{
	(e: 'region-clicked', payload: SimilarRegionClickedPayload): void;
}>();

type CarouselRef = InstanceType<typeof Carousel>;

const carouselContainerRef = ref<CarouselRef | null>(null);
const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef,
	cyclic: true
});

const showSliderSelector = computed<boolean>(
	() => scrollableSlidesNumber.value > 1
);

const getSkeletonClass = computed<string>(() =>
	props.isLoading ? 'h-7 w-1/2 md:w-[359px]' : ''
);

const isRegionLoading = computed<boolean>(
	() => props.isLoading || !props.regionsReport?.length
);

const { smallerOrEqual, greaterOrEqual } = useBreakpoints(breakpointsTailwind);

function getSkeletonNumber() {
	switch (true) {
		case smallerOrEqual('sm').value:
			return 1;
		case smallerOrEqual('md').value:
			return 2;
		case smallerOrEqual('xl').value:
			return 3;
		default:
			return 4;
	}
}

function handleRegionClicked(payload: RegionClickedPayload, index: number) {
	emit('region-clicked', {
		...payload,
		card_position: index
	});
}
</script>

<template>
	<section class="w-full bg-white">
		<Container
			:class="
				cn(
					'!max-w-full flex-1 overflow-hidden bg-white px-0 py-8 md:py-[60px]',
					props.containerClass
				)
			"
		>
			<header class="flex flex-col items-start gap-4 px-5 pb-6 md:px-0 md:pb-9">
				<h2
					class="flex items-center font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary md:text-4xl md:tracking-[-0.045rem]"
				>
					{{ title }}
				</h2>
			</header>

			<div class="flex w-full flex-col gap-6 md:gap-9">
				<div
					v-if="isRegionLoading"
					class="flex h-[300px] w-full items-center justify-center gap-5 px-5 md:h-[378px] md:px-0"
				>
					<PropertyCardSkeleton
						v-for="index in getSkeletonNumber()"
						:key="`region-card-skeleton-${index}`"
						variant="whiteBackground"
					/>
				</div>

				<template v-else>
					<div class="flex w-full flex-col gap-6">
						<Carousel
							ref="carouselContainerRef"
							class="w-full"
							:plugins="[WheelGesturesPlugin()]"
							:opts="{ align: 'start' }"
						>
							<CarouselContent class="w-full gap-[18px] pl-5 md:pl-0">
								<CarouselItem
									v-for="(region, index) in regionsReport"
									:key="region.region"
									class="min-h-fit basis-11/12 sm:basis-[45%] lg:basis-[32%] xl:basis-[24%]"
								>
									<RegionCard
										:region="region.region"
										:property-count="region.propertyCount"
										:top-property-types="region.topPropertyTypes"
										:city="region.city"
										:state="region.state"
										@region-clicked="
											payload => handleRegionClicked(payload, index)
										"
									/>
								</CarouselItem>
							</CarouselContent>
						</Carousel>

						<div
							class="flex h-auto w-full flex-col-reverse items-start justify-between gap-6 px-5 md:mx-0 md:px-0 lg:flex-row lg:items-center"
						>
							<Skeleton
								:is-active="isRegionLoading"
								variant="white"
								:class="cn(getSkeletonClass, 'w-full')"
							>
								<div
									v-if="showSliderSelector"
									class="flex w-full items-center gap-3"
								>
									<div
										ref="carouselItemSelectorContainerRef"
										class="flex w-full flex-wrap justify-end gap-2 md:gap-3"
									>
										<CarouselItemSelector
											v-for="(_, index) in scrollableSlidesNumber"
											:key="`selector-${index}`"
											:active="currentCarouselItemIndex === index"
											:delay="2000"
											:dynamic-width="carouselDynamicItemWidth"
											variant="dark"
											@click="scrollTo(index)"
										/>
									</div>
									<div class="flex max-w-[66px] gap-[10px] md:gap-2">
										<CarouselGenericAction
											variant="dark"
											@click="scrollTo(currentCarouselItemIndex - 1)"
										>
											<IconMaterialSymbolsLightChevronLeft
												width="28px"
												height="28px"
											/>
										</CarouselGenericAction>
										<CarouselGenericAction
											variant="dark"
											@click="scrollTo(currentCarouselItemIndex + 1)"
										>
											<IconMaterialSymbolsLightChevronRight
												width="28px"
												height="28px"
											/>
										</CarouselGenericAction>
									</div>
								</div>
							</Skeleton>
						</div>
					</div>
				</template>
			</div>
		</Container>
	</section>
</template>
